import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Lock } from "lucide-react";

import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { projectQuery } from "@/lib/projectsQuery";

/**
 * A project page for someone who is not on the project: ONE clean refusal,
 * nothing else.
 *
 * QA, 2026-10-28: a rep who opened another company's project URL got the
 * Contacts list's own "You don't have access to this project" — and, around
 * it, a "Project not found" toast (GET /api/projects/:id answered 404) and the
 * page's live Add and Import buttons, which could only fail. The server now
 * answers that read 403 with the one refusal (server/lib/projectAccess.ts), a
 * 403 never toasts (lib/queryClient.ts), and this replaces the page under it:
 * no header actions, no figures, no buttons that cannot work.
 *
 * It reads the same query the page header already reads (projectQuery), so it
 * costs no request. While that answer is out the page renders as usual.
 */
export function ProjectAccessGate({ projectId, children }: { projectId: string | null | undefined; children: ReactNode }) {
  const t = useTranslation();
  const { error } = useQuery(projectQuery(projectId ?? undefined));
  const denied = (error as { status?: number } | null)?.status === 403;
  if (!projectId || !denied) return <>{children}</>;
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center py-section" data-testid="project-access-denied">
      <EmptyState
        icon={Lock}
        title={t("ctcNoAccessTitle", "You don't have access to this project")}
        description={t("ctcNoAccessBody", "Ask a project administrator to add you, or switch to a project you are on.")}
        actions={
          <Button asChild variant="outline" size="sm">
            <Link href="/projects">{t("shellAuthGoToProjects", "Go to Projects")}</Link>
          </Button>
        }
      />
    </div>
  );
}
